/**
 * payrollStage — two words where there were four, and why the list keeps all
 * four anyway.
 *
 * Mikko, 2026-09-13: "im not happy with the process, its overprocess for me
 * and prone to confusion … drafting, issuing, printing etc." He is right. A
 * payslip for a five-person schedule day went through Save drafts, then Issue,
 * then Print, then Mark paid, across three screens, and three of those four
 * steps existed to serve the model rather than the person.
 *
 * SO THE SCREENS NOW KNOW TWO WORDS:
 *
 *   OPEN   still being worked out. Editable. Nobody has been paid.
 *   PAID   the money went. Figures frozen. Reprint any time, Void to reopen.
 *
 * WHY THE LOCK SURVIVED THE CULL. Everything else about issuing was ceremony,
 * but the freeze is not: a payslip somebody is physically holding must not
 * change underneath them, and without a locked state there is no way to tell a
 * corrected figure from an altered one. It simply moved — it used to happen at
 * Issue, it now happens at Pay, which is the moment that actually matters.
 *
 * THE STORED STATUS IS UNTOUCHED. `iSyn2_SubconPayouts` still holds Draft,
 * Issued, Paid and Void, and five thousand imported workbook records still say
 * Paid. Renaming the column's values would mean a migration across live data
 * to make a screen read better, which is the wrong trade every time. So Draft
 * and Issued both READ as open, Pay writes Paid exactly as it always did, and
 * the ledger never learns the new vocabulary.
 *
 * Pure and I/O-free.
 */
import type { PayoutStatus } from "./payout";

/** What a person sees. Two working states, plus the cancelled one. */
export type PayrollStage = "open" | "paid" | "void";

export const STAGE_LABEL: Record<PayrollStage, string> = {
  open: "Open",
  paid: "Paid",
  void: "Void",
};

/**
 * The stored status as one of the two words.
 *
 * Draft and Issued are the same thing to a person: money not yet moved on a
 * payslip they can still change. The distinction was ours, not theirs.
 */
export function stageOf(status: PayoutStatus): PayrollStage {
  if (status === "Paid") return "paid";
  if (status === "Void") return "void";
  return "open";
}

/** True when the figures can still be edited. */
export const isOpen = (status: PayoutStatus): boolean => stageOf(status) === "open";

/** True when the figures are frozen because the money went. */
export const isPaid = (status: PayoutStatus): boolean => stageOf(status) === "paid";

/**
 * What an open payout is stored as.
 *
 * Draft, because that is what every open payout in the list already says and
 * a second word for the same state is exactly the confusion being removed.
 */
export const OPEN_STATUS: PayoutStatus = "Draft";

/**
 * Money that is owed or has moved — what a day's total sums over.
 *
 * An open payout counts: the work is done and QC passed it, so it IS owed,
 * whether or not anybody has pressed anything. Void counts for nothing.
 */
export function countsAsOwed(status: PayoutStatus): boolean {
  return stageOf(status) !== "void";
}
